import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { api } from "./client";
import type {
  ActivityResponse,
  AffiliateBalance,
  AffiliateCommission,
  AffiliateMeResponse,
  AffiliatePayout,
  AffiliateReferral,
  CatalogResponse,
  OverviewResponse,
  PackagesResponse,
  ProfileResponse,
  PublicConfig,
  PublicOrderResponse,
} from "./types";

/**
 * Bản UI trước gọi /member/overview lại ở cả 4 trang member. React Query gom về
 * một cache key duy nhất nên chỉ còn một request cho toàn phiên.
 */
export function useOverview(): UseQueryResult<OverviewResponse> {
  return useQuery({
    queryKey: ["member", "overview"],
    queryFn: () => api<OverviewResponse>("/member/overview", { authenticated: true }),
  });
}

export function usePackages(): UseQueryResult<PackagesResponse> {
  return useQuery({
    queryKey: ["member", "packages"],
    queryFn: () => api<PackagesResponse>("/member/packages", { authenticated: true }),
  });
}

export function useCatalog(): UseQueryResult<CatalogResponse> {
  return useQuery({
    queryKey: ["member", "catalog"],
    queryFn: () => api<CatalogResponse>("/member/catalog", { authenticated: true }),
  });
}

export function useActivity(): UseQueryResult<ActivityResponse> {
  return useQuery({
    queryKey: ["member", "activity"],
    queryFn: () => api<ActivityResponse>("/member/activity", { authenticated: true }),
  });
}

export function useProfile(): UseQueryResult<ProfileResponse> {
  return useQuery({
    queryKey: ["member", "profile"],
    queryFn: () => api<ProfileResponse>("/member/profile", { authenticated: true }),
  });
}

export function usePublicConfig(): UseQueryResult<PublicConfig> {
  return useQuery({
    queryKey: ["public-config"],
    // Trang công khai vẫn phải dùng được khi endpoint này lỗi, nên trả về mặc
    // định thay vì để query fail và chặn cả form.
    queryFn: async () => {
      try {
        return await api<PublicConfig>("/auth/public-config");
      } catch {
        return { turnstile: { enabled: false }, password_policy: {}, links: {} } satisfies PublicConfig;
      }
    },
    staleTime: Infinity,
  });
}

/** Trạng thái đơn đã chốt — không còn gì để chờ. */
const SETTLED_ORDER_STATUSES = new Set(["paid", "cancelled", "expired", "failed"]);

/**
 * Đơn cho trang trả tiền, tự hỏi lại cho tới khi chốt.
 *
 * Người dùng đang mở app ngân hàng ở cửa sổ khác, nên trang phải tự biết lúc
 * tiền vào mà không cần họ bấm gì. Ngừng hỏi ngay khi đơn đã chốt: một tab bỏ
 * quên vẫn nện máy chủ 12 lần mỗi phút là vô ích, và endpoint này có giới hạn
 * tần suất theo IP.
 */
export function usePublicOrder(orderCode: string): UseQueryResult<PublicOrderResponse> {
  return useQuery({
    queryKey: ["public-order", orderCode],
    enabled: Boolean(orderCode),
    queryFn: () => api<PublicOrderResponse>(`/extension/billing/public/orders/${encodeURIComponent(orderCode)}`),
    refetchInterval: (query) => {
      const status = query.state.data?.order.status;
      return status && SETTLED_ORDER_STATUSES.has(status) ? false : 5000;
    },
    // Mã sai thì thử lại không cứu được gì, chỉ làm người dùng chờ lâu hơn.
    retry: false,
  });
}

/*
  Chương trình đối tác.

  Ba query tách rời thay vì một endpoint tổng: bảng khách và bảng hoa hồng đều
  có thể dài, còn thẻ số dư ở đầu trang là thứ cần tải nhanh và cần làm mới lại
  sau mỗi thao tác. Gộp chung thì một lần bấm "Rút tiền" phải tải lại cả ba bảng.
*/

export function useAffiliate(): UseQueryResult<AffiliateMeResponse> {
  return useQuery({
    queryKey: ["affiliate", "me"],
    queryFn: () => api<AffiliateMeResponse>("/affiliate/me", { authenticated: true }),
  });
}

export function useAffiliateReferrals(enabled: boolean): UseQueryResult<{ items: AffiliateReferral[] }> {
  return useQuery({
    queryKey: ["affiliate", "referrals"],
    enabled,
    queryFn: () => api<{ items: AffiliateReferral[] }>("/affiliate/referrals", { authenticated: true }),
  });
}

export function useAffiliateCommissions(
  enabled: boolean,
): UseQueryResult<{ items: AffiliateCommission[]; balance: AffiliateBalance }> {
  return useQuery({
    queryKey: ["affiliate", "commissions"],
    enabled,
    queryFn: () =>
      api<{ items: AffiliateCommission[]; balance: AffiliateBalance }>("/affiliate/commissions", {
        authenticated: true,
      }),
  });
}

export function useAffiliatePayouts(enabled: boolean): UseQueryResult<{ items: AffiliatePayout[] }> {
  return useQuery({
    queryKey: ["affiliate", "payouts"],
    enabled,
    queryFn: () => api<{ items: AffiliatePayout[] }>("/affiliate/payouts", { authenticated: true }),
  });
}
